/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from "react";
import { OrderStatus, OrderStatusLabel } from "@constant/OrderStatus";
import { FilterTwoTone, PlusCircleTwoTone, ExclamationCircleOutlined } from "@ant-design/icons";
import PageTitle from "@component/PageTitle";
import IntegratedTable from "@component/Table/IntegratedTable";
import { resetExternalOrderSlice } from "@redux/externalOrderSlice";
import {
  onlineOrderState,
  fetchOrderList,
  setOrderListFilter,
  fetchDeleteOrder,
  setHideUserInfo,
} from "@redux/onlineOrderSlice";
import { Button, Row, Radio, Space, message, Modal } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store";
import styled from "styled-components";
import ImportFileModal from "@component/ImportFileModal";
import { hideLoading, showLoading } from "@redux/notifySlice";
import onlineOrderApi, { OrderListFilter } from "@api/throughShipment/onlineOrderApi";
import PopupBackground from "@component/PopupBackground";
import { translateErrors } from "@utils/translateError";
import EyeOffIcon from "@assets/Eye-Off@3x.png";
import EyeOpenIcon from "@assets/Eye-On@3x.png";
import throughShipmentApi, { RegionType, RetailList } from "@api/throughShipment/throughShipmentApi";
import { getWarehouseCode } from "@constant/WarehouseCodeValue";

import Filter from "./Filter";
import EditPopup from "./EditPopup";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;
const CustomButton = styled(Button)`
  margin-right: 10px;
`;
const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 364px;
  padding: 30px 20px 20px 20px;
  background-color: white;
`;
const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 14px;
`;
const ButtonWrapper = styled(Row)`
  margin-top: 24px;
`;
const ModalContent = styled.section``;
const NoticeText = styled.p`
  margin-bottom: 15px;
`;
const EyeIconImg = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
  margin-left: 10px;
`;
const LeftHeader = styled.div`
  display: flex;
  align-items: center;
`;

export default function OnlineOrder() {
  const { orderListFilter, onlineOrderListResult, isFetching, hideUserInfo } = useSelector(onlineOrderState);

  const dispatch = useAppDispatch();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showAddMethod, setShowAddMethod] = useState<boolean>(false);
  const [addMethod, setAddMethod] = useState<number>(1);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [retailerList, setRetailerList] = useState<RetailList[]>([]);

  useEffect(() => {
    dispatch(fetchOrderList(orderListFilter));
  }, [dispatch, orderListFilter]);

  useEffect(() => {
    return () => {
      dispatch(resetExternalOrderSlice());
    };
  }, [dispatch]);

  const columns: ColumnsType<any> = [
    {
      key: "orderNumber",
      dataIndex: "orderNumber",
      title: "訂單編號",
    },
    {
      key: "warehouseCode",
      dataIndex: "warehouseCode",
      title: "出貨倉庫",
      render: (code: string) => {
        const warehouse = getWarehouseCode(code);
        return warehouse ? warehouse.label : code;
      },
    },
    {
      key: "platform",
      dataIndex: "platform",
      title: "通路平台",
    },
    {
      key: "platformNumber",
      dataIndex: "platformNumber",
      title: "平台訂單編號",
    },
    {
      key: "status",
      dataIndex: "status",
      title: "訂單狀態",
      render: (data: OrderStatus, values) => {
        return <div>{OrderStatusLabel[data]}</div>;
      },
    },
    {
      key: "receiverName",
      dataIndex: "receiverName",
      title: "收件人",
      render: (data: string, _) => {
        return <div>{hideUserInfo ? `${data[0]}**` : data}</div>;
      },
    },
    {
      key: "totalEstimatedQty",
      dataIndex: "totalEstimatedQty",
      title: "預計出貨總數量(pcs)",
    },
    {
      key: "estimatedShippingDate",
      dataIndex: "estimatedShippingDate",
      title: "預計出貨日",
    },
    {
      key: "totalActualQty",
      dataIndex: "totalActualQty",
      title: "實際出貨總數量(pcs)",
    },
    {
      key: "shippingDate",
      dataIndex: "shippingDate",
      title: "實際出貨時間",
    },
    {
      key: "staff",
      dataIndex: "staff",
      title: "建立人員",
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "建立時間",
    },
    {
      key: "submittedAt",
      dataIndex: "submittedAt",
      title: "提交時間",
    },
    {
      key: "buttons",
      dataIndex: "buttons",
      title: "",
      render: (_, values) => {
        return (
          <Row justify="end">
            {values.destroyedAt || values.submittedAt ? (
              <Button type="link" onClick={() => window.open(`${window.location.href}/${values.id}/review`)}>
                檢視
              </Button>
            ) : (
              <Button type="link" onClick={() => window.open(`${window.location.href}/${values.id}`)}>
                編輯
              </Button>
            )}

            <Button type="link" onClick={() => handelDeleteOrder(values.id)} disabled={!!values.destroyedAt}>
              作廢
            </Button>
          </Row>
        );
      },
    },
  ];

  const handelDeleteOrder = (orderId: number) => {
    Modal.warn({
      title: "你確定要作廢這筆資料？",
      okText: "是",
      onOk() {
        dispatch(fetchDeleteOrder(orderId));
      },
      maskClosable: true,
    });
  };

  async function fetchBatchUploadOrderLists(file: File) {
    try {
      dispatch(showLoading());
      const response = await onlineOrderApi.fetchBatchUploadOrderLists(file);
      dispatch(fetchOrderList(orderListFilter));
      message.success("匯入成功");
      setShowPopup(false);
      return response;
    } catch (error: any) {
      const { result } = error;
      const translatedErrors = translateErrors(result);
      if (!(translatedErrors.length > 0)) return null;
      Modal.warning({
        title: "部分商品資料有誤，請修正",
        content: (
          <ModalContent>
            {translatedErrors.map((errorItem: any) => {
              return <NoticeText>{errorItem}</NoticeText>;
            })}
          </ModalContent>
        ),
        okText: "我知道了",
      });

      return null;
    } finally {
      dispatch(hideLoading());
    }
  }

  const fetchRetailerList = useCallback(async () => {
    try {
      const resp = await throughShipmentApi.fetchRetailList({ regionTypes: RegionType.DOMESTIC_ONLINE });
      setRetailerList(resp.results);
    } catch (error) {
      // pass
    }
  }, []);

  useEffect(() => {
    fetchRetailerList();
  }, [fetchRetailerList]);

  return (
    <Wrapper>
      <PageTitle title="線上出貨訂單" />
      <Row justify="space-between">
        <LeftHeader>
          <CustomButton
            type="primary"
            icon={<PlusCircleTwoTone twoToneColor="#1890FF" />}
            onClick={() => {
              setShowAddMethod(true);
              dispatch(setHideUserInfo(true));
            }}
          >
            新增出貨單
          </CustomButton>
          <Button icon={<FilterTwoTone />} onClick={() => setShowFilter(!showFilter)}>
            篩選
          </Button>
          <EyeIconImg
            src={hideUserInfo ? EyeOffIcon : EyeOpenIcon}
            onClick={() => {
              if (hideUserInfo) {
                Modal.confirm({
                  title: "此頁面資訊含有個人資料，為保護個資安全，確定要顯示嗎?",
                  icon: <ExclamationCircleOutlined />,
                  okText: "確定",
                  cancelText: "取消",
                  onOk() {
                    dispatch(setHideUserInfo(!hideUserInfo));
                  },
                });
                return;
              }
              dispatch(setHideUserInfo(!hideUserInfo));
            }}
          />
        </LeftHeader>
      </Row>
      {showFilter && <Filter />}
      {showAddMethod && (
        <PopupBackground close={() => setShowAddMethod(false)}>
          <InnerWrapper>
            <Title>請選擇新增方式</Title>
            <Radio.Group value={addMethod} onChange={(e) => setAddMethod(e.target.value)}>
              <Space direction="vertical">
                <Radio value={1}>單筆新增(出貨單資料與商品分開新增)</Radio>
                <Radio value={2}>批次新增(出貨單資料與商品一起新增)</Radio>
              </Space>
            </Radio.Group>
            <ButtonWrapper justify="end">
              <CustomButton onClick={() => setShowAddMethod(false)}>取消</CustomButton>
              <CustomButton
                type="primary"
                onClick={() => {
                  setShowPopup(true);
                  setShowAddMethod(false);
                }}
              >
                確認
              </CustomButton>
            </ButtonWrapper>
          </InnerWrapper>
        </PopupBackground>
      )}
      {showPopup && addMethod === 1 && <EditPopup retailerList={retailerList} onClose={() => setShowPopup(false)} />}
      {showPopup && addMethod === 2 && (
        <ImportFileModal
          csvErrorMessage=""
          closePopup={() => setShowPopup(false)}
          clearErrorMessage={() => {}}
          handleUploadFile={fetchBatchUploadOrderLists}
          templatePath="admin/template/xls/online_order_template.xlsx"
          fileType=".xlsx"
          noticeText="註：匯入上限為 1000 筆"
        />
      )}
      <IntegratedTable
        isPagination
        paginationConfig={{
          totalCount: onlineOrderListResult.count,
          updateFilterParams: (filter: OrderListFilter) => dispatch(setOrderListFilter(filter)),
          filterParams: orderListFilter,
        }}
        dataSource={onlineOrderListResult.results}
        loading={isFetching}
        columns={columns}
        scroll={{ x: "max-content" }}
        onRow={(record: any) => ({
          className: record.destroyedAt ? "ur-disabled-text" : "",
        })}
        rowKey={(record: any) => record.id.toString()}
      />
    </Wrapper>
  );
}
